// pages/JewishThoughtSourceDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import Accordion from '../components/Accordion';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

export default function JewishThoughtSourceDetail() {
  const { subtopicId, id } = useParams();
  const [source, setSource] = useState(null);
  const [subtopic, setSubtopic] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Get source details
        const res = await fetchWithAuth(`/api/jewish-thought-subtopic-sources/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) setSource(data);

        // Get subtopic name
        const subtopicRes = await fetchWithAuth(`/api/jewish-thought-subtopics/${subtopicId}/`);
        if (subtopicRes.ok) {
          const subtopicData = await subtopicRes.json();
          if (!ignore) setSubtopic(subtopicData);
        }

        // Get source files
        const filesRes = await fetchWithAuth(`/api/jewish-thought-subtopic-sources/${id}/files/`);
        if (filesRes.ok) {
          const filesData = await filesRes.json();
          if (!ignore) setFiles(filesData);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת המקור');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, subtopicId]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!source) return null;

  // Separate video files from other files (including YouTube URLs)
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
  const videoFiles = files.filter(file => {
    const url = file.file || file.url || '';
    const isVideoFile = videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
    const isYouTubeUrl = extractYouTubeVideoId(url) !== null;
    return isVideoFile || isYouTubeUrl;
  });
  const otherFiles = files.filter(file => {
    const url = file.file || file.url || '';
    const isVideoFile = videoExtensions.some(ext => url.toLowerCase().endsWith(ext));
    const isYouTubeUrl = extractYouTubeVideoId(url) !== null;
    return !isVideoFile && !isYouTubeUrl;
  });

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/jewish-thought/subtopics', label: 'מחשבת ישראל' },
          { to: `/jewish-thought/subtopics/${subtopicId}`, label: subtopic?.name || '...' },
          { label: source.name },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {source.name}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to={`/jewish-thought/subtopics/${subtopicId}`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לנושא
        </Link>
      </p>

      {/* Explanation accordion */}
      <Accordion title="ציטוט המקור" content={source.explanation} className="mb-8" />

      {/* Video section */}
      {videoFiles.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">סרטוני הסבר</h3>
          {videoFiles.map(video => (
            <VideoPlayer key={video.id} video={video} />
          ))}
        </section>
      )}

      {/* Other files section */}
      {otherFiles.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">קבצים</h3>
          <div className="grid-autofit">
            {otherFiles.map(file => (
              <ResourceCard key={file.id} item={file} />
            ))}
          </div>
        </section>
      )}

      {files.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין קבצים להצגה.
        </p>
      )}
    </div>
  );
}

// Utility function to extract YouTube video ID from various URL formats
function extractYouTubeVideoId(url) {
  if (!url) return null;
  
  // Patterns for different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
}

// YouTube Embed Component
function YoutubeEmbed({ videoId, title }) {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube.com/embed/${videoId}`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title={title || 'סרטון YouTube'}
        loading="lazy"
      />
    </div>
  );
}

function VideoPlayer({ video }) {
  const videoUrl = video.file || video.url;
  const youtubeVideoId = extractYouTubeVideoId(videoUrl);
  const isYouTube = youtubeVideoId !== null;

  return (
    <div className="mb-6 bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden">
      <div className="aspect-video bg-black">
        {isYouTube ? (
          <YoutubeEmbed 
            videoId={youtubeVideoId} 
            title={video.title || 'סרטון YouTube'}
          />
        ) : (
          <video
            controls
            className="w-full h-full"
            preload="metadata"
          >
            <source src={videoUrl} type="video/mp4" />
            הדפדפן שלך אינו תומך בתגית וידאו.
          </video>
        )}
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
            {video.title || 'סרטון ללא כותרת'}
          </h4>
          {!isYouTube && (
            <a
              href={videoUrl}
              download
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              הורדה
            </a>
          )}
          {isYouTube && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              צפייה ב-YouTube
            </a>
          )}
        </div>
        {video.author && (
          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-2">{video.author}</p>
        )}
      </div>
    </div>
  );
}

function ResourceCard({ item }) {
  const { openResourceDetail } = useResourceDetailModal();
  const hasFile = !!item.file;
  const isExternal = !!item.url && !hasFile;
  const downloadUrl = hasFile
    ? `/api/jewish-thought-subtopic-source-files/${item.id}/download/`
    : item.file || item.url;

  const handleOpen = () => {
    if (!item.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }

    const modalItem = {
      id: item.id,
      model_type: 'jewish_thought_source_file',
      title: item.title || 'קובץ ללא כותרת',
      summary: item.explanation || '',
      explanation: item.explanation || '',
      author: item.author || '',
      subjectLabel: item.discipline_name || 'מחשבת ישראל',
      typeLabel: item.resource_type_label || 'קובץ',
      grades: item.grades || [],
      tags: [],
      downloadUrl,
      date: item.created_at || '',
    };
    openResourceDetail(modalItem);
  };

  return (
    <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
      <button
        type="button"
        onClick={handleOpen}
        className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
        style={{ aspectRatio: '3/2' }}
      >
        {item.image ? (
          <img src={item.image} alt={item.title || 'קובץ'} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}
      </button>
      <div className="p-4 md:p-5">
        <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
          <button
            type="button"
            onClick={handleOpen}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
          >
            {item.title || 'קובץ ללא כותרת'}
          </button>
        </h4>
        {item.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{item.author}</p>}
      </div>
    </article>
  );
}
